import {AiToolImpl} from "@me8eon/aiclient";
import {mustBeObjectWithFields, mustBeString} from "@me8eon/validate";
import {ElasticSearchConfig, ElasticSearchHit, ElasticSearchKnnSearchResponse} from "@me8eon/ai.tools.utils";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

/**
 * Converts an Elasticsearch hit to a formatted string.
 * @param hit
 * @param index
 */
const hitToString = (hit: ElasticSearchHit, index: number): string => {
    return `**Document ${index + 1}**:
     ${hit._source.title && `- Title: ${hit._source.title}\n`}
     - (Score: ${hit._score}, 
     - Index: ${hit._index}, 
     - ID: ${hit._id}),
     - Document data: ${JSON.stringify(hit._source, null, 2)}`;
};


type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (config: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

type Executor = (config: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl['execute']



export const executeKnn: Executor = (esConfig, jwtTokenFn) => async (indices, input) => {
    const {
        axios,
        blacklistedFields = ["full_text_embeddings", "vectorisation_model"],
    } = esConfig;
    const {query} = input;
    if (indices.length === 0) throw new Error("No indicies provided to knn tool");
    const top = 3;
    const indiciesAsString = indices.join(",");
    const url = `${EnvironmentConfig.elasticSearchProxyUrl}/${indiciesAsString}/_search`;
    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await jwtTokenFn()}`,
    };
    const body = {
        _source: {
            excludes: blacklistedFields,
        },
        knn: {
            field: "full_text_embeddings",
            query_vector_builder: {
                text_embedding: {
                    model_id: ".multilingual-e5-small_linux-x86_64",
                    model_text: query,
                },
            },
            k: top,
            num_candidates: top + 10,
        },
    };

    const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, {headers: headers});
    const topDocuments = response.data?.hits?.hits?.slice(0, top).map((hit, index) => hitToString(hit, index));
    const content = `Here are the top ${topDocuments.length} documents found:\n\n${topDocuments.join("\n\n")}`;
    return {
        value: content,
    }
};


export const KnnToolImplGenerator: Tool = (esConfig, jwtTokenFn)=> {
    return {
        type: "knn",
        toolDefn: {
            type: 'function',
            name: "knn",
            description: "This tool performs a k-NN search on an Elasticsearch index to retrieve the most relevant documents based on the input query. It returns the top matching documents along with their details.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The search query string used to find relevant documents."
                    }
                },
            },
        },
        validateArgs: mustBeObjectWithFields({query: mustBeString}),
        execute: executeKnn(esConfig, jwtTokenFn),
    };
};

